// UiConfigContext
// ---------------
// A tiny provider for admin-editable UI-behaviour flags stored in `portal_config`
// (one row per key). Loaded ONCE at the app shell and read by deep render sites via
// a hook, so a global on/off flag doesn't have to be prop-drilled through a dozen
// components.
//
// Current flags:
//   • showDeviceDelivery — whether the PER-DEVICE "Delivery Address" section shows on
//     a Device item (wizard, read views, picker). Default OFF (hidden): each request
//     now carries a request-level delivery address, so the per-device one is hidden
//     unless an admin turns it back on at Manage → System Setup → Config. The Sales
//     Order / dispatch always use the request-level address regardless of this flag.
//   • stockTopUpEnabled — whether Stock Top-ups are LIVE for DFV Services. Default OFF:
//     while off, a stock-holding service still sees the New-Request type chooser but the
//     Stock Top-up option is disabled and reads "Not available at the moment"; ON makes it
//     available to all. TEST services get no special-case (13 Sep 2026). Edited at
//     Manage → System Setup → Config; this flag is just the global switch.
//   • byodShowResponders / byodShowShelters — whether the BYOD-lookup ("BYOD AI") button
//     is shown to RESPONDERS (Devices card) / to SHELTERS (wizard device step). Both
//     default OFF (BYOD Stage 3): nothing is user-visible until an admin flips them at
//     Manage → System Setup → AI Searches — intended after the Stage-4 school seed. Each
//     gates one placement independently.
//   • byodResponderCanEdit — whether a RESPONDER may review/edit/save a BYOD result, or only
//     view it (BYOD Part 8). Default OFF: a responder lookup auto-saves the whole-school
//     result and presents the covering band READ-ONLY; corrections are made by an admin via
//     the Edit-School BYOD manager. ON restores the responder review/edit/save flow. This is
//     independent of byodShowResponders (which gates whether the panel appears at all).
//   • byodResponderWholeSchool — whether a responder lookup searches the WHOLE school (ON) or
//     only the current child's year (OFF, default — faster). Part 9. Either way a lookup saves
//     any band not already stored; a fresh band is view-only (no new search).
//   • repeatCheckMonths / repeatCheckTypes — the possible-repeat flag settings (db-210):
//     the look-back window in months (default 12) and the ticked item types (raw stored
//     array; absent = all types). Edited at Manage → System Setup → Access. Read via
//     useRepeatCheckSettings(), which normalises them for request_repeat_matches().
//
// Default is HIDDEN/OFF (false) so a component rendered without a provider (e.g. some
// unit tests) hides the section; tests that need it shown pass an explicit `value`.

import React from "react";
import { getConfigValue } from "../data/portalConfig";
import {
  normaliseRepeatMonths,
  REPEAT_MONTHS_KEY,
  REPEAT_TYPES_KEY,
  repeatTypesParam,
} from "../services/repeatCheck";

export const SHOW_DEVICE_DELIVERY_KEY = "show_device_delivery";
export const STOCK_TOP_UP_ENABLED_KEY = "stock_top_up_enabled";
export const BYOD_SHOW_RESPONDERS_KEY = "byod_show_responders";
export const BYOD_SHOW_SHELTERS_KEY = "byod_show_shelters";
export const BYOD_RESPONDER_CAN_EDIT_KEY = "byod_responder_can_edit";
export const BYOD_RESPONDER_WHOLE_SCHOOL_KEY = "byod_responder_whole_school";

const DEFAULT_UI_CONFIG = {
  showDeviceDelivery: false,
  stockTopUpEnabled: false,
  byodShowResponders: false,
  byodShowShelters: false,
  byodResponderCanEdit: false,
  byodResponderWholeSchool: false,
  repeatCheckMonths: null,
  repeatCheckTypes: null,
};

const UiConfigContext = React.createContext(DEFAULT_UI_CONFIG);

// Provider. Loads the flag(s) from portal_config on mount. Pass `value` to inject a
// fixed config (tests / stories) and skip the async load.
export function UiConfigProvider({ children, value }) {
  const [cfg, setCfg] = React.useState(value || DEFAULT_UI_CONFIG);

  React.useEffect(
    function () {
      // Fixed override (tests) — don't hit the DB, just use what was passed.
      if (value) {
        setCfg(value);
        return;
      }
      let active = true;
      Promise.all([
        getConfigValue(SHOW_DEVICE_DELIVERY_KEY),
        getConfigValue(STOCK_TOP_UP_ENABLED_KEY),
        getConfigValue(BYOD_SHOW_RESPONDERS_KEY),
        getConfigValue(BYOD_SHOW_SHELTERS_KEY),
        getConfigValue(BYOD_RESPONDER_CAN_EDIT_KEY),
        getConfigValue(BYOD_RESPONDER_WHOLE_SCHOOL_KEY),
        getConfigValue(REPEAT_MONTHS_KEY),
        getConfigValue(REPEAT_TYPES_KEY),
      ]).then(function ([sdd, stu, bsr, bss, bre, bws, rcm, rct]) {
        // Absent key / anything but explicit true → the default (off).
        if (active)
          setCfg({
            showDeviceDelivery: sdd === true,
            stockTopUpEnabled: stu === true,
            byodShowResponders: bsr === true,
            byodShowShelters: bss === true,
            byodResponderCanEdit: bre === true,
            byodResponderWholeSchool: bws === true,
            repeatCheckMonths: rcm ?? null,
            repeatCheckTypes: rct ?? null,
          });
      });
      return function () {
        active = false;
      };
    },
    [value]
  );

  return <UiConfigContext.Provider value={cfg}>{children}</UiConfigContext.Provider>;
}

// Read the whole UI config.
export function useUiConfig() {
  return React.useContext(UiConfigContext);
}

// Convenience: just the per-device delivery visibility flag.
export function useShowDeviceDelivery() {
  return React.useContext(UiConfigContext).showDeviceDelivery === true;
}

// Convenience: the global Stock-Top-up availability flag (the per-shelter TEST
// override is applied at the call site, which knows the shelter code).
export function useStockTopUpEnabled() {
  return React.useContext(UiConfigContext).stockTopUpEnabled === true;
}

// Convenience: whether the BYOD-lookup button shows to responders / to shelters.
// Both default OFF; each gates one placement (BYOD Stage 3).
export function useByodShowResponders() {
  return React.useContext(UiConfigContext).byodShowResponders === true;
}

export function useByodShowShelters() {
  return React.useContext(UiConfigContext).byodShowShelters === true;
}

// Convenience: whether a responder may edit/save a BYOD result (ON) or only view it
// (OFF, default — the lookup auto-saves and shows read-only; admins edit). BYOD Part 8.
export function useByodResponderCanEdit() {
  return React.useContext(UiConfigContext).byodResponderCanEdit === true;
}

// Convenience: whether a responder BYOD lookup searches the WHOLE school (ON) or only
// the current child's year (OFF, default — faster). BYOD Part 9.
export function useByodResponderWholeSchool() {
  return React.useContext(UiConfigContext).byodResponderWholeSchool === true;
}

// Convenience: the possible-repeat flag settings (db-210), normalised for the database
// function — months as a whole number ≥ 1 (default 12); types as null (= check every
// type) or the list of ticked types.
export function useRepeatCheckSettings() {
  const cfg = React.useContext(UiConfigContext);
  return {
    months: normaliseRepeatMonths(cfg.repeatCheckMonths),
    types: repeatTypesParam(cfg.repeatCheckTypes),
  };
}
